import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import cx from 'classnames'
import Styles from './SegmentedControl.module.scss'

import { SegmentedControlItemProps, SegmentedControlProps } from './Types'
import { useButton } from '@react-aria/button'

const Item = memo<SegmentedControlItemProps>(
  ({
    registerRef,
    name,
    icon: Icon,
    text,
    onClick,
    active,
    slimButton,
    mediumButton,
    flexButton,
    isDisabled,
    iconClassName,
    buttonClassName,
  }) => {
    const elRef = useRef<HTMLButtonElement | null>(null)
    const iconColorClass = useMemo(() => {
      if (!active) {
        return Styles.inactiveIconColor
      }
      return iconClassName ?? Styles.activeIconColor
    }, [active, iconClassName])

    const { buttonProps } = useButton(
      {
        isDisabled: isDisabled,
        onPress: () => onClick(name),
      },
      elRef,
    )

    return (
      <button
        {...buttonProps}
        aria-label={name}
        className={cx(
          Styles.item,
          {
            [Styles.itemActive]: active && !isDisabled,
            [Styles.flexButton]: flexButton,
            [Styles.mediumButton]: mediumButton,
            [Styles.slimButton]: slimButton,
          },
          buttonClassName,
        )}
        ref={(node) => {
          elRef.current = node
          registerRef(name, node)
        }}
        disabled={isDisabled}
      >
        {Icon && <Icon className={cx(Styles.icon, iconColorClass, { [Styles.withText]: !!text })} />}
        {text}
      </button>
    )
  },
)

function elementWidth(element: HTMLElement) {
  return element.getBoundingClientRect().width
}

type ItemsRefMap = { [key: string]: HTMLButtonElement | null }

/*
 * A segmented control is a linear set of two or more segments, each of which
 * functions as a mutually exclusive button. Within the control, all segments
 * are equal in width. The segmented control spans the entire width of its container.
 *
 * It's a controlled a component, so it receives `activeItem`
 * and a callback `onItemClick` to update the value.
 */
const SegmentedControl = ({
  items,
  activeItem,
  onItemClick,
  slimButtons = false,
  isDisabled = false,
  mediumButtons = false,
  flexButtons = false,
  buttonClassName,
  highlighterClassName,
  rootClassName,
}: SegmentedControlProps) => {
  const [showHighlighter, setShowHighlighter] = useState(false)
  const itemsRefs = useRef<ItemsRefMap>({})
  const registerRef = useCallback((name: string, ref: any) => (itemsRefs.current[name] = ref), [])
  const currentActiveItemEl = itemsRefs.current[activeItem]
  const currentItem = items.find((item) => item.name === activeItem)
  const [activeElemWidth, setActiveElemWidth] = useState(0)

  useEffect(() => {
    if (!currentActiveItemEl) return

    setActiveElemWidth(elementWidth(currentActiveItemEl))

    const currentItemResizeObserver = new ResizeObserver(() => setActiveElemWidth(elementWidth(currentActiveItemEl)))
    currentItemResizeObserver.observe(currentActiveItemEl)

    return () => currentItemResizeObserver.disconnect()
  }, [currentActiveItemEl])

  useLayoutEffect(() => {
    setShowHighlighter(true)
  }, [currentActiveItemEl])

  return (
    <div className={cx(Styles.root, rootClassName)}>
      {currentActiveItemEl && showHighlighter && (
        <div
          className={cx(Styles.highlighter, highlighterClassName, currentItem?.highlighterClassName)}
          style={{
            transform: `translateX(${currentActiveItemEl.offsetLeft}px)`,
            width: `${activeElemWidth}px`,
          }}
        />
      )}
      {items.map((item) => (
        <Item
          registerRef={registerRef}
          name={item.name}
          key={item.name}
          icon={item.icon}
          iconClassName={item.iconClassName}
          buttonClassName={buttonClassName}
          text={item.text}
          onClick={onItemClick}
          active={activeItem === item.name}
          slimButton={slimButtons}
          mediumButton={mediumButtons}
          flexButton={flexButtons}
          isDisabled={isDisabled}
        />
      ))}
    </div>
  )
}

export { SegmentedControl }
